import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { logo } from "../../constants/icons";
import { HiOutlineMenu, HiX } from "react-icons/hi";

// Custom Hooks
import { useHeaderProfile } from "../../hooks/header/useHeaderProfile";
import { useHeaderNotifications } from "../../hooks/header/useHeaderNotifications";
import { useHeaderSearch } from "../../hooks/header/useHeaderSearch";
import { useHeaderNavigation } from "../../hooks/header/useHeaderNavigation";
import { useHeaderModals } from "../../hooks/header/useHeaderModals";

// UI Components
import SearchBar from "../../components/header/SearchBar";
import NotificationDropdown from "../../components/header/NotificationDropdown";
import ProfileDropdown from "../../components/header/ProfileDropdown";
import NavigationMenu from "../../components/header/NavigationMenu";
import MegaMenu from "../../components/header/MegaMenu";
import LogoutConfirmModal from "../../components/header/LogoutConfirmModal";
import MobileHeader from "../../components/header/MobileHeader";
import LanguageSelector from "../common/LanguageSelector";
import ContactSupportModal from "../common/ContactSupportModal";
import PlayerHeaderSubmenu from "../player/PlayerHeaderSubmenu";

// Utils
import { getMenuItems } from "../../components/header/menuItems";
import { ROLES } from "../../config/roles";

const Header = () => {
  const { user, roles, currentRole, kycVerified } = useSelector(
    (state) => state.auth
  );

  // Profile Hook
  const {
    profileData,
    isLoading,
    getProfileDisplay,
    getInitials,
    getNameByRole,
    getInitialsForRole,
    getProfilePicAccordingToRole,
  } = useHeaderProfile();

  // Modals Hook
  const {
    isSubmenuOpen,
    setIsSubmenuOpen,
    isProfileOpen,
    setIsProfileOpen,
    isLeagueSwitcherOpen,
    setIsLeagueSwitcherOpen,
    isNotificationOpen,
    setIsNotificationOpen,
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    isMobileProfileOpen,
    setIsMobileProfileOpen,
    isMobileSearchOpen,
    setIsMobileSearchOpen,
    showSupportModal,
    setShowSupportModal,
  } = useHeaderModals();

  // Search Hook
  const {
    searchQuery,
    setSearchQuery,
    searchType,
    setSearchType,
    isSearchOpen,
    setIsSearchOpen,
    handleSearch,
    searchTypes,
  } = useHeaderSearch();

  // Navigation Hook
  const {
    navigationItems,
    megaCategories,
    quickAccessPills,
    whatsNewUnread,
    showLogoutConfirm,
    setShowLogoutConfirm,
    handleLogout,
    handleConfirmLogout,
    isActive,
    handleRoleSwitch,
    isRoleSwitching,
    isAgencyOnUI,
    canEditRoles,
    canViewTenders,
  } = useHeaderNavigation(profileData, kycVerified);

  // Notifications Hook
  const {
    unreadCount,
    notifications,
    recentNotifications,
    handleNotificationClick,
    handleMarkAllAsRead,
    formatNotificationTime,
  } = useHeaderNotifications(isNotificationOpen);

  console.log("Header - Auth State:", {
    user,
    roles,
    currentRole,
    kycVerified,
  });

  useEffect(() => {
    console.log("profileData", profileData);
    console.log("notifications", notifications);
  }, [profileData, notifications]);

  // Get menu items
  const menuItems = getMenuItems(currentRole, canEditRoles, canViewTenders);

  // Get profile images and initials for different roles
  const currentProfileImage = getProfilePicAccordingToRole();
  const currentProfileInitials = getInitialsForRole();
  const clubProfileImage = getProfilePicAccordingToRole(ROLES.ClubAdmin);
  const clubProfileInitials = getInitialsForRole(ROLES.ClubAdmin);

  const canSwitchRoles =
    roles?.includes(ROLES.AgencyAdmin) && roles?.includes(ROLES.Agent);

  const agentProfileImage = canSwitchRoles
    ? getProfilePicAccordingToRole(ROLES.Agent)
    : null;
  const agentProfileInitials = canSwitchRoles
    ? getInitialsForRole(ROLES.Agent)
    : "";
  const agencyProfileImage = canSwitchRoles
    ? getProfilePicAccordingToRole(ROLES.AgencyAdmin)
    : null;
  const agencyProfileInitials = canSwitchRoles
    ? getInitialsForRole(ROLES.AgencyAdmin)
    : "";

  // Anchor the mega-menu panel + overlay to the live bottom edge of the header
  // so there is never a gap (or overlap) regardless of the header's height.
  const headerRef = useRef(null);
  const [menuAnchorTop, setMenuAnchorTop] = useState(0);
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const measure = () => setMenuAnchorTop(el.getBoundingClientRect().bottom);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, []);

  return (
    <>
      <header ref={headerRef} className="bg-white border-b">
        <div className="px-4 md:px-6 lg:px-8 py-2">
          <div className="hidden md:flex items-center w-full h-16 gap-4 lg:gap-6">
            {/* Left Section - Logo and Navigation */}
            <div className="flex items-center gap-3 lg:gap-4 flex-shrink-0">
              {/* Logo */}
              <Link to="/" className="flex-shrink-0">
                <img src={logo} alt="TransferPitch" className="h-10 w-auto" />
              </Link>

              {/* Primary Navigation */}
              <NavigationMenu
                navigationItems={navigationItems}
                isActive={isActive}
              />
            </div>

            {/* Middle Section - Search Container (Flexible, with a floor).
                min-w keeps the search box from being the sole shrink victim
                and collapsing to zero at narrow desktop widths — the root of
                the "search disappears at half-width" bug. The floor is sized
                so the row still clears a half-width window on a 1920px display
                (~960px) without horizontal overflow. */}
            <div className="flex-1 min-w-0 flex items-center justify-center px-2">
              <SearchBar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                searchType={searchType}
                setSearchType={setSearchType}
                searchTypes={searchTypes}
                isSearchOpen={isSearchOpen}
                setIsSearchOpen={setIsSearchOpen}
                handleSearch={handleSearch}
              />
            </div>

            {/* Right Section - compact controls in the design vocabulary */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {/* Language Selector — compact "EN" chip */}
              <div className="flex-shrink-0">
                <LanguageSelector compact />
              </div>

              {/* Menu, Notifications, Profile */}
              <div className="flex gap-1.5 items-center justify-end flex-shrink-0">
                <button
                  className={`relative w-[38px] h-[38px] rounded-[10px] flex items-center justify-center hover:bg-[#F3F6FB] transition-colors ${
                    isSubmenuOpen ? "bg-[#F3F6FB]" : ""
                  }`}
                  onClick={() => setIsSubmenuOpen(!isSubmenuOpen)}
                  aria-label="Menu"
                  aria-haspopup="true"
                  aria-expanded={isSubmenuOpen}
                >
                  {isSubmenuOpen ? (
                    <HiX className="w-[22px] h-[22px] text-[#0F1419]" />
                  ) : (
                    <HiOutlineMenu className="w-[22px] h-[22px] text-[#0F1419]" />
                  )}
                  {/* What's New unread signal, visible before opening the menu
                      (D-003 dot). Cleared once the feed page is opened. */}
                  {whatsNewUnread && !isSubmenuOpen && (
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#2283FF] border-[1.5px] border-white" />
                  )}
                </button>

                {/* Notification Bell */}
                <NotificationDropdown
                  isNotificationOpen={isNotificationOpen}
                  setIsNotificationOpen={setIsNotificationOpen}
                  unreadCount={unreadCount}
                  recentNotifications={recentNotifications}
                  notifications={notifications}
                  handleNotificationClick={handleNotificationClick}
                  handleMarkAllAsRead={handleMarkAllAsRead}
                  formatNotificationTime={formatNotificationTime}
                />

                {/* Profile Dropdown */}
                <ProfileDropdown
                  isProfileOpen={isProfileOpen}
                  setIsProfileOpen={setIsProfileOpen}
                  profileData={profileData}
                  isLoading={isLoading}
                  getProfileDisplay={getProfileDisplay}
                  currentProfileImage={currentProfileImage}
                  currentProfileInitials={currentProfileInitials}
                  clubProfileImage={clubProfileImage}
                  clubProfileInitials={clubProfileInitials}
                  agentProfileImage={agentProfileImage}
                  agentProfileInitials={agentProfileInitials}
                  agencyProfileImage={agencyProfileImage}
                  agencyProfileInitials={agencyProfileInitials}
                  canSwitchRoles={canSwitchRoles}
                  currentRole={currentRole}
                  isAgencyOnUI={isAgencyOnUI}
                  isRoleSwitching={isRoleSwitching}
                  handleRoleSwitch={handleRoleSwitch}
                  handleLogout={handleLogout}
                  menuItems={menuItems}
                  setShowSupportModal={setShowSupportModal}
                  PlayerHeaderSubmenu={PlayerHeaderSubmenu}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Header */}
        <MobileHeader
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          isMobileProfileOpen={isMobileProfileOpen}
          setIsMobileProfileOpen={setIsMobileProfileOpen}
          isMobileSearchOpen={isMobileSearchOpen}
          setIsMobileSearchOpen={setIsMobileSearchOpen}
          isNotificationOpen={isNotificationOpen}
          setIsNotificationOpen={setIsNotificationOpen}
          unreadCount={unreadCount}
          profileData={profileData}
          getProfileDisplay={getProfileDisplay}
          getProfilePicAccordingToRole={getProfilePicAccordingToRole}
          getInitialsForRole={getInitialsForRole}
          getNameByRole={getNameByRole}
          handleLogout={handleLogout}
          menuItems={menuItems}
          navigationItems={navigationItems}
          megaCategories={megaCategories}
          quickAccessPills={quickAccessPills}
          isActive={isActive}
          handleRoleSwitch={handleRoleSwitch}
          canSwitchRoles={canSwitchRoles}
          currentRole={currentRole}
          isAgencyOnUI={isAgencyOnUI}
          isRoleSwitching={isRoleSwitching}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          // The overlay covers the whole screen, so leaving it open after a
          // search hides the results the search just produced. An empty
          // submit searches nothing and keeps the overlay, as before.
          handleSearch={(e) => {
            if (handleSearch(e)) setIsMobileSearchOpen(false);
          }}
          searchType={searchType}
          setSearchType={setSearchType}
          searchTypes={searchTypes}
          notifications={notifications}
          recentNotifications={recentNotifications}
          handleNotificationClick={handleNotificationClick}
          handleMarkAllAsRead={handleMarkAllAsRead}
          formatNotificationTime={formatNotificationTime}
        />

        <ContactSupportModal
          isOpen={showSupportModal}
          onClose={() => setShowSupportModal(false)}
          prefaceMessage={"How can we help you?"}
          onSuccess={() => setShowSupportModal(false)}
        />
      </header>

      {/* Mega menu (DoD 22) — full-width categorised panel, opened by the ☰. */}
      <MegaMenu
        isOpen={isSubmenuOpen}
        onClose={() => setIsSubmenuOpen(false)}
        anchorTop={menuAnchorTop}
        categories={megaCategories}
        pills={quickAccessPills}
        navItems={navigationItems}
        isActive={isActive}
      />

      {/* Click outside handlers */}
      {(isSearchOpen ||
        isSubmenuOpen ||
        isLeagueSwitcherOpen ||
        isNotificationOpen ||
        isProfileOpen) && (
        <div
          className="fixed inset-0 z-40 bg-transparent"
          onClick={(e) => {
            // Only close if clicking the overlay itself
            if (e.target === e.currentTarget) {
              setIsSearchOpen(false);
              setIsSubmenuOpen(false);
              setIsLeagueSwitcherOpen(false);
              setIsNotificationOpen(false);
              setIsProfileOpen(false);
            }
          }}
        />
      )}

      {/* Logout Confirmation Modal */}
      <LogoutConfirmModal
        showLogoutConfirm={showLogoutConfirm}
        setShowLogoutConfirm={setShowLogoutConfirm}
        handleConfirmLogout={handleConfirmLogout}
      />
    </>
  );
};

export default Header;
